import { useState, useMemo, useCallback } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import LabelRequired from 'components/FormFields/LabelRequired';

const ITEMS_PER_PAGE = 10;

const SearchableBusinessSelect = ({
  businesses = [],
  businessesFields = [],
  index,
  field: _field,
  handleChangeBusinesses,
  formMethods,
  disabled = false,
  sx = {},
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [displayedCount, setDisplayedCount] = useState(ITEMS_PER_PAGE);
  const [isOpen, setIsOpen] = useState(false);

  // Get display label for a business (brand name - location name)
  const getBusinessDisplayLabel = useCallback((business) => {
    if (!business) return '';
    const brandName = business.companyName || '';
    const locationName = business.locationInfo?.[0]?.locationName || '';
    if (locationName) {
      return `${brandName} - ${locationName}`;
    }
    return brandName;
  }, []);

  // Find the currently selected business object
  const getSelectedBusiness = useCallback(
    (uuid) => {
      return businesses.find((b) => b?.uuid === uuid) || null;
    },
    [businesses]
  );

  // Filter businesses based on search term (only when dropdown is open and user is typing)
  const filteredBusinesses = useMemo(() => {
    if (!isOpen || !inputValue) return businesses;
    const searchTerm = inputValue.toLowerCase();
    return businesses.filter((business) => {
      const displayLabel = getBusinessDisplayLabel(business).toLowerCase();
      return displayLabel.includes(searchTerm);
    });
  }, [businesses, inputValue, isOpen, getBusinessDisplayLabel]);

  // Slice to show only displayedCount items
  const displayedOptions = useMemo(() => {
    return filteredBusinesses.slice(0, displayedCount);
  }, [filteredBusinesses, displayedCount]);

  // Check if a business is already selected in another field
  const isOptionDisabled = useCallback(
    (option) => {
      return businessesFields?.some(
        (item, idx) => item?.businessUuid === option?.uuid && idx !== index
      );
    },
    [businessesFields, index]
  );

  // Handle scroll to load more items
  const handleScroll = useCallback(
    (event) => {
      const listboxNode = event.currentTarget;
      const { scrollTop, scrollHeight, clientHeight } = listboxNode;

      // Load more when scrolled near bottom (within 50px)
      if (scrollHeight - scrollTop - clientHeight < 50) {
        if (displayedCount < filteredBusinesses.length) {
          setDisplayedCount((prev) =>
            Math.min(prev + ITEMS_PER_PAGE, filteredBusinesses.length)
          );
        }
      }
    },
    [displayedCount, filteredBusinesses.length]
  );

  // Handle input change (search)
  const handleInputChange = useCallback((event, newInputValue, reason) => {
    if (reason === 'input') {
      setInputValue(newInputValue);
      setDisplayedCount(ITEMS_PER_PAGE);
    } else if (reason === 'reset') {
      // When selection is made or cleared, don't update inputValue here
      // Let the value prop handle displaying the selected item
    }
  }, []);

  // Handle selection change
  const handleChange = useCallback(
    (event, newValue) => {
      // Simulate the event structure expected by handleChangeBusinesses
      const syntheticEvent = {
        target: {
          value: newValue?.uuid || '',
        },
      };
      handleChangeBusinesses(syntheticEvent, index);
      setInputValue(''); // Clear search after selection
    },
    [handleChangeBusinesses, index]
  );

  // Handle dropdown open/close
  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setInputValue(''); // Clear search when opening
    setDisplayedCount(ITEMS_PER_PAGE);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setInputValue(''); // Clear search when closing
  }, []);

  return (
    <Controller
      name={`businesses[${index}].businessUuid`}
      control={formMethods?.control}
      render={({ field: controllerField }) => {
        const selectedBusiness = getSelectedBusiness(controllerField.value);

        return (
          <Autocomplete
            disabled={disabled}
            open={isOpen}
            onOpen={handleOpen}
            onClose={handleClose}
            options={displayedOptions}
            value={selectedBusiness}
            onChange={handleChange}
            inputValue={
              isOpen ? inputValue : getBusinessDisplayLabel(selectedBusiness)
            }
            onInputChange={handleInputChange}
            getOptionLabel={(option) => getBusinessDisplayLabel(option)}
            getOptionDisabled={isOptionDisabled}
            isOptionEqualToValue={(option, value) =>
              option?.uuid === value?.uuid
            }
            ListboxProps={{
              onScroll: handleScroll,
              style: { maxHeight: 300 },
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.uuid}>
                {getBusinessDisplayLabel(option)}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={<LabelRequired>{t('Business')}</LabelRequired>}
                size="small"
                InputLabelProps={{ shrink: true }}
                placeholder={isOpen ? t('Search business...') : ''}
              />
            )}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                height: '40px',
                paddingRight: '9px !important',
              },
              '& .MuiAutocomplete-input': {
                padding: '7.5px 4px 7.5px 6px !important',
              },
              ...sx,
            }}
            noOptionsText={t('No businesses found')}
            loadingText={t('Loading...')}
            forcePopupIcon={true}
            clearIcon={null}
          />
        );
      }}
    />
  );
};

export default SearchableBusinessSelect;
